/**
 * Dashboard OUTCOMES panel — answers "is it making money?" with the honest
 * numbers:
 *
 *  - Realised Outcomes card: net realised P&L over a selectable 7d/30d/all
 *    window, win rate, payoff ratio vs the break-even payoff the hit rate
 *    demands (red when below — the honest signal), a cumulative realised-P&L
 *    sparkline, the exit-type breakdown in plain language, and top
 *    winners/losers.
 *  - Open Positions card: each open position with its protection state (stop
 *    levels from the live SAFETY_EXIT thresholds, trail armed, held-for) and
 *    an honest "snapshot from <time>" staleness note.
 *
 * Self-contained: fetches GET /api/outcomes and /api/outcomes/positions
 * itself, so it drops into the Dashboard as a single section.
 */

import { useState, useEffect } from 'react';
import { Box, Flex, Text, SimpleGrid } from '@chakra-ui/react';
import { api } from '@/lib/api';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  exitActionLabel,
  formatSignedGbp,
  payoffAssessment,
  holdLabel,
  snapshotLabel,
  buildSparkline,
} from '@/lib/outcomes-format';
import type { OutcomesSummary, OpenPositionsReport, OpenPositionReport } from '@market-buddy/shared';

type WindowKey = '7' | '30' | 'all';

const WINDOW_OPTIONS: { key: WindowKey; label: string }[] = [
  { key: '7', label: '7D' },
  { key: '30', label: '30D' },
  { key: 'all', label: 'All' },
];

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function WindowSelector({ value, onChange }: { value: WindowKey; onChange: (w: WindowKey) => void }) {
  return (
    <Flex gap="1">
      {WINDOW_OPTIONS.map((opt) => (
        <Box
          key={opt.key}
          as="button"
          onClick={() => onChange(opt.key)}
          px="8px"
          py="2px"
          borderRadius="md"
          border="1px solid"
          borderColor={value === opt.key ? 'border.brand' : 'transparent'}
          bg={value === opt.key ? 'brand.50' : 'transparent'}
          color={value === opt.key ? 'text.brand' : 'text.tertiary'}
          fontSize="11px"
          fontWeight="600"
          cursor="pointer"
        >
          {opt.label}
        </Box>
      ))}
    </Flex>
  );
}

function EquitySparkline({ summary }: { summary: OutcomesSummary }) {
  const width = 280;
  const height = 48;
  const values = summary.daily.map((d) => d.cumulativePnl);
  const spark = buildSparkline(values, width, height);
  if (!spark) return null;

  const final = values[values.length - 1];
  const stroke = final >= 0 ? 'var(--chakra-colors-status-success-text)' : 'var(--chakra-colors-status-error-text)';

  return (
    <Box mt="3" mb="1">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        role="img"
        aria-label="Cumulative realised P&L"
      >
        {spark.zeroY !== null && (
          <line
            x1="0"
            y1={spark.zeroY}
            x2={width}
            y2={spark.zeroY}
            stroke="currentColor"
            opacity="0.2"
            strokeDasharray="3 3"
          />
        )}
        <polyline points={spark.points} fill="none" stroke={stroke} strokeWidth="1.5" />
      </svg>
      <Flex justify="space-between">
        <Text fontSize="10px" color="text.tertiary">{summary.daily[0]?.date}</Text>
        <Text fontSize="10px" color="text.tertiary">
          {summary.daily[summary.daily.length - 1]?.date}
        </Text>
      </Flex>
    </Box>
  );
}

function ExitBreakdownTable({ summary }: { summary: OutcomesSummary }) {
  if (summary.byAction.length === 0) return null;
  return (
    <Box mt="3">
      <Text fontSize="12px" fontWeight="500" color="text.secondary" mb="2">By exit type</Text>
      <Flex direction="column" gap="1">
        <Flex gap="2" fontSize="10px" color="text.tertiary">
          <Text flex="1" fontSize="10px" color="text.tertiary">Exit</Text>
          <Text w="36px" textAlign="right" fontSize="10px" color="text.tertiary">Trades</Text>
          <Text w="70px" textAlign="right" fontSize="10px" color="text.tertiary">P&L</Text>
          <Text w="52px" textAlign="right" fontSize="10px" color="text.tertiary">Avg %</Text>
        </Flex>
        {summary.byAction.map((a) => (
          <Flex key={a.action} gap="2" align="center">
            <Text flex="1" fontSize="12px" color="text.primary">{exitActionLabel(a.action)}</Text>
            <Text w="36px" textAlign="right" fontSize="12px" fontFamily="mono" color="text.secondary">
              {a.trades}
            </Text>
            <Text
              w="70px"
              textAlign="right"
              fontSize="12px"
              fontFamily="mono"
              color={a.totalPnl >= 0 ? 'status.success.text' : 'status.error.text'}
            >
              {formatSignedGbp(a.totalPnl)}
            </Text>
            <Text w="52px" textAlign="right" fontSize="12px" fontFamily="mono" color="text.tertiary">
              {a.avgPnlPercent >= 0 ? '+' : ''}{a.avgPnlPercent.toFixed(1)}%
            </Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  );
}

function TopMovers({ summary }: { summary: OutcomesSummary }) {
  if (summary.topWinners.length === 0 && summary.topLosers.length === 0) return null;
  return (
    <SimpleGrid columns={2} gap="3" mt="3">
      <Box>
        <Text fontSize="12px" fontWeight="500" color="text.secondary" mb="1">Top winners</Text>
        {summary.topWinners.length === 0 ? (
          <Text fontSize="12px" color="text.tertiary">None</Text>
        ) : (
          summary.topWinners.map((w) => (
            <Flex key={w.symbol} justify="space-between">
              <Text fontSize="12px" color="text.primary">{w.symbol}</Text>
              <Text fontSize="12px" fontFamily="mono" color="status.success.text">
                {formatSignedGbp(w.totalPnl)}
              </Text>
            </Flex>
          ))
        )}
      </Box>
      <Box>
        <Text fontSize="12px" fontWeight="500" color="text.secondary" mb="1">Top losers</Text>
        {summary.topLosers.length === 0 ? (
          <Text fontSize="12px" color="text.tertiary">None</Text>
        ) : (
          summary.topLosers.map((l) => (
            <Flex key={l.symbol} justify="space-between">
              <Text fontSize="12px" color="text.primary">{l.symbol}</Text>
              <Text fontSize="12px" fontFamily="mono" color="status.error.text">
                {formatSignedGbp(l.totalPnl)}
              </Text>
            </Flex>
          ))
        )}
      </Box>
    </SimpleGrid>
  );
}

function OutcomesCard({
  summary,
  windowKey,
  onWindowChange,
}: {
  summary: OutcomesSummary | null;
  windowKey: WindowKey;
  onWindowChange: (w: WindowKey) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <Text fontSize="14px" fontWeight="500" color="text.secondary">Realised Outcomes</Text>
        <WindowSelector value={windowKey} onChange={onWindowChange} />
      </CardHeader>

      {!summary ? (
        <Skeleton width="100%" height={120} />
      ) : summary.tradeCount === 0 ? (
        <Text fontSize="13px" color="text.tertiary">
          No closed trades in this window yet.
        </Text>
      ) : (
        <>
          {/* Headline: the number that answers "is it making money?" */}
          <Flex align="baseline" gap="3" wrap="wrap">
            <Text
              fontSize="28px"
              fontWeight="700"
              lineHeight="1"
              fontFamily="mono"
              color={summary.netPnl >= 0 ? 'status.success.text' : 'status.error.text'}
            >
              {formatSignedGbp(summary.netPnl)}
            </Text>
            <Text fontSize="12px" color="text.tertiary">
              {summary.tradeCount} trades &middot; {summary.wins}W / {summary.losses}L &middot; win rate {summary.winRate.toFixed(0)}%
            </Text>
          </Flex>

          {/* The honest signal: payoff vs what the hit rate needs */}
          <Flex gap="2" mt="3" wrap="wrap" align="center">
            {(() => {
              const p = payoffAssessment(summary.payoffRatio, summary.breakEvenPayoff);
              return <Badge variant={p.tone}>{p.label}</Badge>;
            })()}
            <Text fontSize="11px" color="text.tertiary">
              avg win {summary.avgWinPercent >= 0 ? '+' : ''}{summary.avgWinPercent.toFixed(2)}% &middot; avg loss {summary.avgLossPercent.toFixed(2)}%
            </Text>
          </Flex>

          <EquitySparkline summary={summary} />
          <ExitBreakdownTable summary={summary} />
          <TopMovers summary={summary} />
        </>
      )}
    </Card>
  );
}

function PositionRow({ p }: { p: OpenPositionReport }) {
  const held = holdLabel(p.holdCandles);
  return (
    <Box py="2" borderBottom="1px solid" borderColor="border.secondary" _last={{ borderBottom: 'none' }}>
      <Flex justify="space-between" align="center" mb="1">
        <Flex align="baseline" gap="2">
          <Text fontSize="13px" fontWeight="600" color="text.primary">{p.symbol ?? p.ticker}</Text>
          <Text fontSize="11px" color="text.tertiary">
            {p.quantity} @ {p.avgPrice.toFixed(2)}
          </Text>
          {held && <Text fontSize="11px" color="text.tertiary">held {held}</Text>}
        </Flex>
        <Text
          fontSize="12px"
          fontFamily="mono"
          color={p.unrealisedPnl >= 0 ? 'status.success.text' : 'status.error.text'}
        >
          {formatSignedGbp(p.unrealisedPnl)} ({p.unrealisedPnlPercent >= 0 ? '+' : ''}{p.unrealisedPnlPercent.toFixed(1)}%)
        </Text>
      </Flex>
      <Flex gap="2" wrap="wrap" align="center">
        {p.protection === 'unprotected' ? (
          <Badge variant="error">Unprotected</Badge>
        ) : (
          <>
            <Text fontSize="11px" color="text.tertiary" fontFamily="mono">
              Stop {p.softStopPrice.toFixed(2)} &middot; Backstop {p.backstopPrice.toFixed(2)}
            </Text>
            {p.brokerStop && (
              <Badge variant="neutral">Broker stop {p.brokerStop.stopLoss.toFixed(2)}</Badge>
            )}
            {p.trailArmed && p.trailStopPrice !== null && (
              <Badge variant="success">Trail armed @ {p.trailStopPrice.toFixed(2)}</Badge>
            )}
          </>
        )}
      </Flex>
    </Box>
  );
}

function OpenPositionsCard({ report }: { report: OpenPositionsReport | null }) {
  return (
    <Card>
      <CardHeader>
        <Flex align="center" gap="2">
          <Text fontSize="14px" fontWeight="500" color="text.secondary">Open Positions</Text>
          {report && <Badge variant={report.positions.length > 0 ? 'brand' : 'neutral'}>{report.positions.length}</Badge>}
        </Flex>
        {report && (
          <Text fontSize="11px" color="text.tertiary">
            soft stop -{report.thresholds.softStopPct}% &middot; backstop -{report.thresholds.maxLossPct}% &middot; trail +{report.thresholds.trailArmPct}%/-{report.thresholds.trailGivebackPct}%
          </Text>
        )}
      </CardHeader>

      {!report ? (
        <Skeleton width="100%" height={80} />
      ) : (
        <>
          {report.positions.length === 0 ? (
            <Text fontSize="13px" color="text.tertiary">
              {report.fetchedAt === null
                ? 'No position snapshot available.'
                : 'No open positions in the latest snapshot.'}
            </Text>
          ) : (
            <Flex direction="column">
              {report.positions.map((p) => (
                <PositionRow key={p.ticker} p={p} />
              ))}
            </Flex>
          )}
          <Text fontSize="11px" color="text.tertiary" mt="3">
            {snapshotLabel(report.fetchedAt)}
          </Text>
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function OutcomesSection() {
  const [windowKey, setWindowKey] = useState<WindowKey>('30');
  const [summary, setSummary] = useState<OutcomesSummary | null>(null);
  const [positions, setPositions] = useState<OpenPositionsReport | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    api
      .get<OutcomesSummary>(`/api/outcomes?days=${windowKey}`)
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [windowKey]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<OpenPositionsReport>('/api/outcomes/positions')
      .then((data) => { if (!cancelled) setPositions(data); })
      .catch(() => { /* card shows its own skeleton/empty state */ });
    return () => { cancelled = true; };
  }, []);

  if (failed) {
    return (
      <Card>
        <Text fontSize="13px" color="text.tertiary">Outcomes unavailable right now.</Text>
      </Card>
    );
  }

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} gap="4">
      <OutcomesCard summary={summary} windowKey={windowKey} onWindowChange={setWindowKey} />
      <OpenPositionsCard report={positions} />
    </SimpleGrid>
  );
}
